import { useEffect } from 'react';
// UI imports
import PrivatePageLayout from '../../../layout/privatePageLayout/PrivatePageLayout';
import RichTextEditor from '../../../components/stateful/richTextEditor/RichTextEditor';
import DropDown from '../../../components/stateless/dropDown/DropDown';
import './CodeManagement.scss';
import CustomButton from '../../../components/stateless/customButton/CustomButton';
import ExpansionPanel from '../../../components/stateless/expansionPanel/ExpansionPanel';
import HtmlRender from '../../../components/stateless/htmlRender/HtmlRender';
import TextField from '../../../components/stateful/textField/TextField';
import FileUpload from '../../../components/stateful/fileUpload/FileUpload';
import { BlobContainer } from '../../../utilities/enums/BlobContainer';
import { Loader } from '@progress/kendo-react-indicators';
import { useCodeManagementControls } from './CodeManagementControls';
import CustomDialog from '../../../components/stateless/dialog/CustomDialog';
import DatePicker from '../../../components/stateless/datePicker/DatePicker';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import moment from 'moment';
import { APISettings } from '../../../config/ApiSettings';
import { AddAgencyDialog } from './components/AddAgencyDialog';
import { AddTitleDialog } from './components/AddTitlesDialog';
import { AgencyEditDialog } from './components/AgencyEditDialog';

// import { useNotificationHelper } from '../../../utilities/helpers/NotificationHelper';

function CodeManagement() {
  // all state and functions behavior is handled in the CodeManagementControls
  //#region functions and state
  const {
    // functions
    saveFile,
    deleteFile,
    handleTitleSelectChange,
    onSave,
    handleAgencySelectChange,
    handleChapterSelectChange,
    handleChapterNameChange,
    handleChapterNumberChange,
    toggleEdit,
    toggleEmergencyRegulation,
    toggleRenew,
    toggleAgencyAddDialog,
    toggleAgencyEditDialog,
    toggleTitleAddDialog,
    handleFileUploadResponseMessage,
    handleTitleNameChange,
    handleTitleNumberChange,
    handleRecordStatusChange,
    handleDateFiledChange,
    handleEffectiveDateChange,
    handleRenewDateChange,
    confirmRenewDate,
    //redux
    getAllAgencies,
    getAllTitles,
    getRecordStatuses,
    // state
    titleValues,
    titleList,
    chapterValues,
    agencyValues,
    recordStatuses,
    dispatch,
    selectedAgency,
    selectedTitle,
    selectedChapter,
    selectedRecordStatus,
    selectedDateFiled,
    selectedEffectiveDate,
    editOpen,
    chapterFiles,
    chaptersLoading,
    editTitleOpen,
    editAgencyOpen,
    emergencyRegulationOpen,
    emergencyRegulation,
    renew,
    renewDate,
    renewed,
    toggleEditTitleDialog,
    updateAgencyTitle,
    addAgencyOpen,
    addTitleOpen,
    isSosUser,
  } = useCodeManagementControls();
  //#endregion

  const baseUrl = new APISettings().getApiURL();

  //#region useState
  useEffect(() => {
    if (titleValues.length < 1) {
      dispatch(getAllTitles());
    }
    if (agencyValues.length < 1) {
      dispatch(getAllAgencies());
    }
    if (recordStatuses.length < 1) {
      dispatch(getRecordStatuses());
    }
  }, [
    dispatch,
    titleValues,
    selectedTitle,
    agencyValues,
    recordStatuses,
    getAllTitles,
    getAllAgencies,
    getRecordStatuses,
  ]);
  //#endregion

  const renderCodeManagement = () => {
    return (
      <div className="body code-management">
        {selectedTitle ? (
          //#region dialog
          <CustomDialog
            open={editTitleOpen}
            title={'Edit Title'}
            toggleDialog={toggleEditTitleDialog}
            approveAction={updateAgencyTitle}
            cancelAction={toggleEditTitleDialog}
          >
            <div className="code-textfield">
              <TextField
                onChange={(value: number) => {
                  handleTitleNumberChange(value);
                }}
                title={'Title Number'}
                value={selectedTitle.titleNumber ?? 'none'}
                fullwidth
                numeric
              />
              <TextField
                onChange={(value: string) => {
                  handleTitleNameChange(value);
                }}
                title={'Title Name'}
                value={selectedTitle.titleName ?? 'none'}
                fullwidth
                hasSuffix
              />
            </div>
          </CustomDialog>
        ) : //#endregion
        null}

        <AddAgencyDialog
          open={addAgencyOpen}
          closeDialog={toggleAgencyAddDialog}
        />
        {selectedAgency ? (
          <AddTitleDialog
            agency={selectedAgency}
            open={addTitleOpen}
            closeDialog={toggleTitleAddDialog}
          />
        ) : null}
        <AgencyEditDialog
          agency={selectedAgency}
          open={editAgencyOpen}
          closeDialog={toggleAgencyEditDialog}
        />
        <CustomDialog
          open={emergencyRegulationOpen}
          title="Emergency Regulation"
          toggleDialog={toggleEmergencyRegulation}
          hideButtons
          size="large"
        >
          {emergencyRegulation && (
            <div style={{ margin: '20px' }}>
              <HtmlRender
                value={emergencyRegulation.regulationHtml}
                className=""
              />
              <div>
                <DatePicker
                  onChange={(date: DatePickerChangeEvent) => {
                    handleRenewDateChange(date.value);
                  }}
                  label={'Effective To'}
                  size="medium"
                  min={moment(new Date(emergencyRegulation.effectiveTo)).add(1, 'days').toDate()}
                  max={moment(new Date(emergencyRegulation.effectiveTo)).add(90, 'days').toDate()}
                  readOnly={!renew || renewed || emergencyRegulation.renewCount > 0}
                  existingDate={new Date(emergencyRegulation.effectiveTo)}
                />
                {!renew ? (
                  <CustomButton
                    onClick={toggleRenew}
                    disabled={emergencyRegulation.renewCount > 0}
                  >
                    Renew
                  </CustomButton>
                ) : (
                  <CustomButton
                    onClick={confirmRenewDate}
                    disabled={renewed || !renewDate || renewDate === null}
                  >
                    Set New Effective To Date
                  </CustomButton>
                )}
              </div>
            </div>
          )}
        </CustomDialog>

        {selectedChapter && editOpen ? (
          //#region Edit Chapter View
          <>
            <CustomButton
              onClick={toggleEdit}
              icon="chevron-left"
              size="small"
            >
              Back
            </CustomButton>
            <div className="code-textfield d-flex">
              <div className="flex-column">
                <TextField
                  size="large"
                  readOnly={false}
                  required={false}
                  onChange={(event: any) => handleChapterNumberChange(event)}
                  title="Chapter Number"
                  hasSuffix
                  value={selectedChapter.chapterNumber}
                />
                <TextField
                  size="large"
                  readOnly={false}
                  required={false}
                  onChange={(event: any) => handleChapterNameChange(event)}
                  title="Chapter Name"
                  hasSuffix
                  value={selectedChapter.chapterName}
                />

                <div className="code-record-row">
                  <DropDown
                    size="fullwidth"
                    label="Record Status"
                    value={selectedRecordStatus}
                    listItems={recordStatuses}
                    onSelect={handleRecordStatusChange}
                    dataKey="id"
                    textField="recordStatusName"
                    idName="id"
                  />

                  <DatePicker
                    onChange={handleDateFiledChange}
                    label="Date Filed"
                    existingDate={selectedDateFiled}
                    size={'medium'}
                    placeholderText=""
                  />

                  <DatePicker
                    onChange={handleEffectiveDateChange}
                    label="Effective Date"
                    existingDate={selectedEffectiveDate}
                    size={'medium'}
                    placeholderText=""
                  />
                </div>
              </div>
            </div>

            <div className="editor-container">
              <div>
                <RichTextEditor
                  content={selectedChapter ? selectedChapter.chapterHtml : ''}
                  height={'70vh'}
                  onSave={onSave}
                />
              </div>
              <div>
                <FileUpload
                  containerName={BlobContainer.chapterImageContainer}
                  type={'image'}
                  existingFiles={chapterFiles}
                  saveFile={saveFile}
                  deleteFile={deleteFile}
                  notification={handleFileUploadResponseMessage}
                  allowedTypes="image"
                />
              </div>
            </div>
          </>
        ) : (
          //#endregion
          //#region Default View
          <>
            <div className="edit-button-group">
              <div>
                <CustomButton
                  size="small"
                  onClick={toggleAgencyAddDialog}
                  icon="plus"
                >
                  Add Agency
                </CustomButton>
                <CustomButton
                  size="small"
                  onClick={toggleAgencyEditDialog}
                  disabled={!selectedAgency}
                  icon="pencil"
                >
                  Edit Agency
                </CustomButton>
              </div>
              <div>
                <CustomButton
                  size="small"
                  onClick={toggleTitleAddDialog}
                  disabled={!selectedAgency}
                  icon="plus"
                >
                  Add Title
                </CustomButton>
                <CustomButton
                  size="small"
                  onClick={toggleEditTitleDialog}
                  disabled={!selectedTitle}
                  icon="pencil"
                >
                  Edit Title
                </CustomButton>
              </div>
            </div>
            <DropDown
              size="medium"
              label="Agency"
              value={selectedAgency}
              listItems={agencyValues}
              onSelect={handleAgencySelectChange}
              dataKey="id"
              textField="agencyName"
              // idName="id"
            />
            <DropDown
              size="medium"
              label="Title"
              value={selectedTitle}
              listItems={titleList}
              onSelect={handleTitleSelectChange}
              dataKey="id"
              textField="titleName"
              idName="titleNumber"
            />
            {selectedTitle ? (
              <div>
                <p>Chapters</p>
                {chaptersLoading ? (
                  <>
                    Loading <Loader type="converging-spinner" />
                  </>
                ) : (
                  chapterValues.map((chapter: any, index: number) => {
                    return (
                        <ExpansionPanel
                            key={chapter.id}
                            item={chapter}
                            title={
                                <div style={{ display: 'flex', flexDirection: 'row' }}>
                                    {'Chapter ' +
                                        chapter.chapterNumber +
                                        ': ' +
                                        (chapter.chapterName ? chapter.chapterName : '')}
                                    {chapter.emergencyRegulationId && (
                                        <div style={{ marginLeft: '5px' }}>
                                            <CustomButton
                                                onClick={() => {
                                                    toggleEmergencyRegulation(chapter.emergencyRegulationId);
                                                }}
                                                themeColor="error"
                                                icon="add"
                                                size="small"
                                            />
                                        </div>
                                    )}
                                </div>
                            }
                            fullwidth
                        >
                            <CustomButton onClick={() => {}} themeColor="success" size="small" icon="pdf">
                                <a
                                    className="hidden-anchor"
                                    style={{ color: 'white' }}
                                    href={`${baseUrl}/fileStorage/GetAsByteArray/certificates-of-adoption/${chapter.certificateOfAdoptionBlobName}`}
                                >
                                    Certificate of Adoption
                                </a>
                            </CustomButton>
                            <CustomButton
                                onClick={(event) => {
                                    toggleEdit();
                                    handleChapterSelectChange(chapter);
                                }}
                                size="small"
                            >
                                Edit Chapter
                            </CustomButton>
                            <HtmlRender value={chapter.chapterHtml} className="" />
                        </ExpansionPanel>
                    );
                  })
                )}
              </div>
            ) : null}
          </>
          //#endregion
        )}
      </div>
    );
  };

  return <PrivatePageLayout>{isSosUser && renderCodeManagement()}</PrivatePageLayout>;
}

export default CodeManagement;
